import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import {
  ShipmentResponse,
  ShipmentSettingResponse,
  ShipmentAddRequest,
  ShipmentUpdateRequest,
  ShipmentStatusHistoryResponse,
  CalculatedRoutePointResponse,
  ShipmentAlertResponse,
  ShipmentSensorAlertHistoryResponse,
  AvailableDeviceResponse,
  ShipmentAlertHistoryResponse,
  ShipmentStatusJudgeResponse,
} from '@entities/shipment';
import { ApiService } from '@services/api.service';
import { HttpParams } from '@angular/common/http';
import { DeviceInfoResponse } from '@entities/deviceInfo';
import { AlertHistoryResponse } from '@entities/alert';

@Injectable({
  providedIn: 'root',
})
export class ShipmentService {
  /**
   * NOTE:
   * add API は現行バックエンド仕様未反映のため一時的にモックを返す。
   * API反映後は false に変更すると実API呼び出しへ戻せる。
   */
  constructor(private api: ApiService) {}

  /**
   * 配送設定の一覧を取得する
   *
   * @param startDateRange 開始日範囲
   * @param arrivalDateRange 到着日範囲
   * @param scheduleStartDateRange 開始予定日範囲
   * @param scheduleArrivalDateRange 到着予定日範囲
   * @param departureId 出発地の位置情報ID
   * @param waypointId 経由地の位置情報ID
   * @param destinationId 到着地の位置情報ID
   * @returns 配送設定の一覧
   */
  findList(
    startDateRange: string[] = [],
    arrivalDateRange: string[] = [],
    scheduleStartDateRange: string[] = [],
    scheduleArrivalDateRange: string[] = [],
    departureId?: string,
    destinationId?: string,
    shipmentStatus?: string,
  ): Observable<ShipmentResponse[]> {
    const url = '/shipments';
    let params = new HttpParams();

    // startDateRangeをクエリパラメータに追加
    startDateRange.forEach((date) => {
      params = params.append('start_date_range', date);
    });

    // arrivalDateRangeをクエリパラメータに追加
    arrivalDateRange.forEach((date) => {
      params = params.append('arrival_date_range', date);
    });

    // scheduleStartDateRangeをクエリパラメータに追加
    scheduleStartDateRange.forEach((date) => {
      params = params.append('schedule_start_date_range', date);
    });

    // scheduleArrivalDateRangeをクエリパラメータに追加
    scheduleArrivalDateRange.forEach((date) => {
      params = params.append('schedule_arrival_date_range', date);
    });

    // 出発地・到着地での検索
    if (departureId) {
      params = params.append('departure_id', departureId);
    }
    if (destinationId) {
      params = params.append('destination_id', destinationId);
    }
    if (shipmentStatus) {
      params = params.append('shipment_status', shipmentStatus);
    }

    return this.api.get<ShipmentResponse[]>(url, { params });
  }

  /**
   * 配送設定一覧を取得する（設定情報のみ）
   *
   * @returns 配送設定一覧
   */
  findSettingList(): Observable<ShipmentSettingResponse[]> {
    const url = '/shipments/settings';
    return this.api.get<ShipmentSettingResponse[]>(url);
  }

  /**
   * 配送設定を追加する
   *
   * @param request 追加内容
   */
  add(request: ShipmentAddRequest): Observable<ShipmentResponse> {
    const url = '/shipments';
    return this.api.post<ShipmentResponse>(url, request);
  }

  /**
   * 配送設定を更新する
   *
   * @param shipmentId 配送設定ID
   * @param request 更新内容
   */
  update(
    shipmentId: string,
    request: ShipmentUpdateRequest,
  ): Observable<ShipmentResponse> {
    const url = `/shipments/${shipmentId}`;
    return this.api.put<ShipmentResponse>(url, request);
  }

  /**
   * 配送設定を削除する
   *
   * @param shipmentIdList 配送設定ID
   */
  delete(shipmentIdList: string[]): Observable<void> {
    const url = `/shipments/_delete`;
    return this.api.post<void>(url, shipmentIdList);
  }

  /**
   * 配送ステータスの一覧を取得する
   *
   * @param shipmentIdList 配送IDリスト
   * @param skipLoading ローディング表示をスキップするか
   * @returns 配送設定の一覧
   */
  findStatusList(
    shipmentIdList: string[],
    skipLoading = false,
  ): Observable<ShipmentStatusHistoryResponse[]> {
    const url = '/shipments/status/history/_search';
    return this.api.post<ShipmentStatusHistoryResponse[]>(
      url,
      shipmentIdList,
      {},
      skipLoading,
    );
  }

  /**
   * ルートの到着予想を計算する
   *
   * @param shipmentId 配送ID
   * @returns 計算されたルート要素の一覧
   */
  calcRoute(shipmentId: string): Observable<CalculatedRoutePointResponse[]> {
    const url = `/shipments/${shipmentId}/_calc_route`;
    return this.api.get<CalculatedRoutePointResponse[]>(url);
  }

  /**
   * 配送ステータスを準備完了にする
   *
   * @param shipmentId 配送ID
   * @returns 準備完了にできたか
   */
  updateToReady(shipmentId: string): Observable<ShipmentStatusJudgeResponse> {
    const url = `/shipments/${shipmentId}/status/_ready`;
    return this.api.post<ShipmentStatusJudgeResponse>(url, {});
  }

  /**
   * 配送ステータスを配送完了にする
   *
   * @param shipmentId 配送ID
   */
  updateToInterrupt(shipmentId: string): Observable<void> {
    const url = `/shipments/${shipmentId}/status/_interrupt`;
    return this.api.post<void>(url, {});
  }

  /**
   * 利用可能なデバイス一覧を取得する
   *
   * @param startDate 出発予定日
   * @param arrivalDate 到着予定日
   * @param frequency 繰り返し設定タイプ
   * @param intervalDays 繰り返し設定の繰り返し間隔
   * @param weekdayList 繰り返し設定の毎週設定での選択した曜日のIDリスト
   * @param specificDates 繰り返し設定の複数日付設定での選択した日付のリスト
   * @param frequencyEndDate 繰り返し設定の繰り返し終了日
   * @param skipLoading ローディング表示をスキップするか
   * @returns 利用可能なデバイス一覧
   */
  findAvailableDeviceList(
    options: {
      startDate?: string;
      arrivalDate?: string;
      frequency?: string;
      intervalDays?: number;
      weekdayList?: number[];
      specificDates?: string[];
      frequencyEndDate?: string;
      shipmentId?: string;
      deviceId?: string;
      skipLoading?: boolean;
    } = {},
  ): Observable<AvailableDeviceResponse[]> {
    const {
      startDate = '',
      arrivalDate = '',
      frequency = '',
      intervalDays = 0,
      weekdayList = [],
      specificDates = [],
      frequencyEndDate = '',
      shipmentId,
      deviceId,
      skipLoading = true,
    } = options;
    const url = '/shipments/devices';
    let params = new HttpParams();
    if (startDate) {
      params = params.append('start_datetime', startDate);
    }
    if (arrivalDate) {
      params = params.append('arrival_datetime', arrivalDate);
    }
    if (frequency) {
      params = params.append('frequency', frequency);
    }
    if (intervalDays) {
      params = params.append('interval_days', intervalDays);
    }
    if (shipmentId) {
      params = params.append('shipment_id', shipmentId);
    }
    if (deviceId) {
      params = params.append('device_id', deviceId);
    }

    weekdayList.forEach((weekday) => {
      params = params.append('weekdays', weekday);
    });
    specificDates.forEach((specificDate) => {
      params = params.append('specific_dates', specificDate);
    });

    if (frequencyEndDate) {
      params = params.append('frequency_end_date', frequencyEndDate);
    }

    return this.api.get<AvailableDeviceResponse[]>(
      url,
      { params },
      skipLoading,
    );
  }

  /**
   * 配送センサーアラートの一覧を取得する
   *
   * @param shipmentIdList 配送IDリスト
   * @param skipLoading ローディング表示をスキップするか
   * @returns 配送センサーアラート履歴の一覧
   */
  findSensorAlertList(
    shipmentIdList: string[],
    skipLoading = false,
  ): Observable<ShipmentSensorAlertHistoryResponse[]> {
    const url = '/shipments/sensor-alerts/history/_search';
    return this.api.post<ShipmentSensorAlertHistoryResponse[]>(
      url,
      shipmentIdList,
      {},
      skipLoading,
    );
  }

  /**
   * 期間を指定して配送センサーアラートの一覧を取得する
   *
   * @param historyFrom 期間の開始時刻
   * @param historyTo 期間の終了時刻
   * @param skipLoading ローディング表示をスキップするか
   * @returns 配送センサーアラート履歴の一覧
   */
  findSensorAlertListByRange(
    historyFrom: string,
    historyTo: string,
    skipLoading = false,
  ): Observable<ShipmentSensorAlertHistoryResponse[]> {
    const url = '/shipments/sensor-alerts/history';

    let params = new HttpParams();
    params = params.append('history_from', historyFrom);
    params = params.append('history_to', historyTo);

    return this.api.get<ShipmentSensorAlertHistoryResponse[]>(
      url,
      { params },
      skipLoading,
    );
  }

  /**
   * 配送センサーアラート履歴を取得する
   *
   * @param shipmentId 配送ID
   * @param skipLoading ローディング表示をスキップするか
   * @returns 配送センサーアラート履歴
   */
  findSensorAlertHistoryById(
    shipmentId: string,
    skipLoading = false,
  ): Observable<AlertHistoryResponse> {
    const url = `/shipments/${shipmentId}/sensor-alerts/history`;
    return this.api.get<AlertHistoryResponse>(url, {}, skipLoading);
  }

  /**
   * 期間を指定して配送セアラートの一覧を取得する
   *
   * @param historyFrom 期間の開始時刻
   * @param historyTo 期間の終了時刻
   * @param skipLoading ローディング表示をスキップするか
   * @returns 配送セアラートの一覧
   */
  findAlertListByRange(
    historyFrom: string,
    historyTo: string,
    skipLoading = false,
  ): Observable<ShipmentAlertHistoryResponse[]> {
    const url = '/shipments/alerts/history';

    let params = new HttpParams();
    params = params.append('history_from', historyFrom);
    params = params.append('history_to', historyTo);

    return this.api.get<ShipmentAlertHistoryResponse[]>(
      url,
      { params },
      skipLoading,
    );
  }

  /**
   * 配送アラートの一覧を取得する
   *
   * @param shipmentIdList 配送IDリスト
   * @param skipLoading ローディング表示をスキップするか
   * @returns 配送セアラートの一覧
   */
  findLatestAlertList(
    shipmentIdList: string[],
    skipLoading = false,
  ): Observable<Record<string, ShipmentAlertResponse>> {
    const url = '/shipments/alerts/latest/_search';
    return this.api.post<Record<string, ShipmentAlertResponse>>(
      url,
      shipmentIdList,
      {},
      skipLoading,
    );
  }

  /**
   * 指定した配送IDの配送アラートの一覧を取得する
   *
   * @param shipmentId 配送ID
   * @param skipLoading ローディング表示をスキップするか
   * @returns 配送セアラートの一覧
   */
  findAlertListById(
    shipmentId: string,
    skipLoading = false,
  ): Observable<ShipmentAlertResponse[]> {
    const url = `/shipments/${shipmentId}/alerts/history`;
    return this.api.get<ShipmentAlertResponse[]>(url, {}, skipLoading);
  }

  /**
   * 指定した配送IDの配送デバイス詳細情報の履歴を取得する
   *
   * @param shipmentId 配送ID
   * @param skipLoading ローディング表示をスキップするか
   * @returns 配送デバイス詳細情報の履歴
   */
  findDeviceInfoHistoryById(
    shipmentId: string,
    skipLoading = false,
  ): Observable<DeviceInfoResponse[]> {
    const url = `/shipments/${shipmentId}/device-info/history`;
    return this.api.get<DeviceInfoResponse[]>(url, {}, skipLoading);
  }
}
